"use client";

import { useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import Image from "next/image";
import { FiMenu, FiX, FiChevronDown, FiExternalLink } from "react-icons/fi";
import LanguageSwitcher from "./LanguageSwitcher";
import MobileMenu from "./MobileMenu";

interface NavItem {
  key: string;
  href: string;
  external?: boolean;
  label?: string;
  children?: { key: string; href: string }[];
}

const navItems: NavItem[] = [
  { key: "about", href: "/about" },
  {
    key: "services",
    href: "/services",
    children: [
      { key: "servicesOverview", href: "/services" },
      { key: "cruisePartners", href: "/services/cruise-partners" },
      { key: "tourOperators", href: "/services/tour-operators" },
      { key: "destinationManagement", href: "/services/destination-management" },
    ],
  },
  {
    key: "brands",
    href: "/brands",
    children: [
      { key: "brandsOverview", href: "/brands" },
      { key: "curacaoTours", href: "/brands/curacao-tours" },
      { key: "atlantisAdventures", href: "/brands/atlantis-adventures" },
      { key: "bonaireTours", href: "/brands/bonaire-tours" },
    ],
  },
  { key: "transfers", href: "/transfers" },
  {
    key: "tours",
    href: "https://www.curacao-tours.com",
    external: true,
    label: "Tours",
  },
  { key: "fleet", href: "/fleet" },
  { key: "groupBooking", href: "/group-booking" },
  { key: "faq", href: "/faq" },
  { key: "contact", href: "/contact" },
];

export default function Navbar() {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-fbtt-gray-light bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 lg:px-6">
          {/* Logo */}
          <Link href="/" className="flex items-center" aria-label="FBTT Travel — home">
            <Image
              src="/images/fbtt-logo.png"
              alt="FBTT Travel"
              width={240}
              height={85}
              className="h-10 w-auto"
              priority
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {navItems.map((item) => (
              <div
                key={item.key}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.key)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-md px-2.5 py-2 text-sm font-medium text-fbtt-navy transition-colors hover:bg-fbtt-light hover:text-fbtt-orange"
                  >
                    {item.label || t(item.key)}
                    <FiExternalLink className="h-3 w-3 opacity-60" />
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    className={`flex items-center gap-1 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-fbtt-light hover:text-fbtt-orange ${
                      pathname === item.href
                        ? "text-fbtt-orange"
                        : "text-fbtt-navy"
                    }`}
                  >
                    {t(item.key)}
                    {item.children && <FiChevronDown className="h-3 w-3" />}
                  </Link>
                )}

                {/* Dropdown */}
                {item.children && openDropdown === item.key && (
                  <div className="absolute left-0 top-full z-50 min-w-[200px] rounded-lg border border-fbtt-gray-light bg-white py-2 shadow-lg">
                    {item.children.map((child) => (
                      <Link
                        key={child.key}
                        href={child.href}
                        className="block px-4 py-2 text-sm text-fbtt-navy transition-colors hover:bg-fbtt-light hover:text-fbtt-orange"
                      >
                        {t(child.key)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              href="/transfers/request"
              className="hidden rounded-full bg-fbtt-orange px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-fbtt-orange-dark hover:shadow-lg hover:shadow-fbtt-orange/25 md:block"
            >
              {t("requestTransfer")}
            </Link>
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded-md p-2 text-fbtt-navy hover:bg-fbtt-light lg:hidden"
              aria-label="Open menu"
            >
              <FiMenu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        open={mobileOpen}
        onClose={closeMobile}
        navItems={navItems}
      />
    </>
  );
}
